import { useEffect, useState } from "react";
import { MagnifyingGlass, Plus, WarningCircle, ArrowsClockwise, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCNav } from "../components/cc/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function CarrierRulesCard({ carriers, rules, onChanged }) {
  const { hasPerm } = useAuth();
  const canRules = hasPerm("commission.rules");
  const [addFor, setAddFor] = useState(null);
  const [form, setForm] = useState({ prefix: "", note: "" });
  const [revokeRule, setRevokeRule] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [aliasFor, setAliasFor] = useState(null);
  const [aliasText, setAliasText] = useState("");

  const addRule = async () => {
    try {
      await api.post(`/commission/carriers/${addFor.id}/prefix-rules`, form);
      toast.success(`Prefix rule '${form.prefix.toUpperCase()}' approved for ${addFor.name}`);
      setAddFor(null); setForm({ prefix: "", note: "" });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add rule"); }
  };

  const revoke = async () => {
    try {
      await api.delete(`/commission/carriers/${revokeRule.carrier_id}/prefix-rules/${revokeRule.id}`,
        { data: { reason: revokeReason } });
      toast.success("Rule revoked");
      setRevokeRule(null); setRevokeReason("");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };

  const saveAliases = async () => {
    try {
      const aliases = aliasText.split(",").map((s) => s.trim()).filter(Boolean);
      await api.put(`/commission/carriers/${aliasFor.id}`, { aliases });
      toast.success("Aliases saved");
      setAliasFor(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save aliases"); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="cc-carrier-rules-card">
      <h2 className="font-medium text-sm mb-1">Carriers, aliases & prefix rules</h2>
      <p className="text-xs text-slate-400 mb-3">Prefix rules make e.g. PGR881031746 equivalent to 881031746 for that carrier only — approved, versioned, revocable. Aliases help EZLynx carrier names resolve (e.g. "Progressive Insurance").</p>
      <div className="space-y-3">
        {carriers.map((c) => {
          const active = rules.filter((r) => r.carrier_id === c.id && r.active);
          return (
            <div key={c.id} className="border border-slate-100 rounded p-2.5">
              <div className="flex items-center justify-between flex-wrap gap-1">
                <p className="text-sm font-medium">{c.name} <span className="text-xs text-slate-400 font-mono">{c.carrier_code}</span></p>
                {canRules && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAliasFor(c); setAliasText((c.aliases || []).join(", ")); }} data-testid={`cc-aliases-btn-${c.id}`}>Aliases</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAddFor(c)} data-testid={`cc-add-rule-btn-${c.id}`}><Plus size={13} className="mr-0.5" /> Prefix rule</Button>
                  </div>
                )}
              </div>
              {(c.aliases || []).length > 0 && (
                <p className="text-xs text-slate-500 mt-1">Aliases: {(c.aliases || []).join(", ")}</p>
              )}
              <div className="flex gap-1.5 flex-wrap mt-1.5">
                {active.length === 0 && <span className="text-xs text-slate-400">No active prefix rules</span>}
                {active.map((r) => (
                  <Badge key={r.id} className="bg-sky-100 text-sky-800 gap-1" title={r.note}>
                    {r.prefix}
                    {canRules && <button onClick={() => setRevokeRule(r)} className="hover:text-red-600" data-testid={`cc-revoke-rule-${r.id}`}><Prohibit size={12} /></button>}
                  </Badge>
                ))}
              </div>
            </div>
          );
        })}
        {carriers.length === 0 && <p className="text-sm text-slate-400">No carriers yet — add them on the Imports tab.</p>}
      </div>

      <Dialog open={!!addFor} onOpenChange={(o) => !o && setAddFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Approve prefix rule — {addFor?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Statements sometimes print the carrier prefix, EZLynx sometimes doesn't. An approved rule makes PREFIX+number ≡ number for this carrier only. Letters and leading zeros are never stripped otherwise.</p>
          <Input placeholder="Prefix, e.g. PGR" value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} data-testid="cc-rule-prefix-input" />
          <Textarea placeholder="Why is this rule correct? (evidence)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="cc-rule-note-input" />
          <Button onClick={addRule} disabled={!form.prefix} data-testid="cc-rule-save-btn">Approve rule</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revokeRule} onOpenChange={(o) => !o && setRevokeRule(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Revoke prefix '{revokeRule?.prefix}'?</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason (required)" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} data-testid="cc-revoke-reason-input" />
          <Button variant="destructive" onClick={revoke} disabled={!revokeReason.trim()} data-testid="cc-revoke-confirm-btn">Revoke rule</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!aliasFor} onOpenChange={(o) => !o && setAliasFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Aliases — {aliasFor?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Comma-separated names this carrier appears as in EZLynx exports.</p>
          <Input placeholder="e.g. Progressive Insurance, Progressive Ins" value={aliasText} onChange={(e) => setAliasText(e.target.value)} data-testid="cc-alias-input" />
          <Button onClick={saveAliases} data-testid="cc-alias-save-btn">Save aliases</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CommissionPoliciesPage() {
  const { hasPerm } = useAuth();
  const [data, setData] = useState({ policies: [], total: 0, unresolved: 0 });
  const [carriers, setCarriers] = useState([]);
  const [rules, setRules] = useState([]);
  const [search, setSearch] = useState("");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (carrierFilter === "unresolved") params.set("unresolved", "1");
    else if (carrierFilter !== "all") params.set("carrier_id", carrierFilter);
    const [p, c, r] = await Promise.all([
      api.get(`/commission/policies?${params}`),
      api.get("/commission/carriers"),
      api.get("/commission/prefix-rules"),
    ]);
    setData(p.data); setCarriers(c.data); setRules(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, [carrierFilter]);

  const resync = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.post("/commission/policies/resync");
      toast.success(`Resynced: ${s.created} created, ${s.updated} updated, ${s.unresolved} carrier-unresolved`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resync failed"); }
    finally { setBusy(false); }
  };

  const carrierName = (id) => carriers.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="commission-policies-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Canonical policies</h1>
          <p className="text-sm text-slate-500 mt-1">Built from validated EZLynx snapshots — {data.total} policies, {data.unresolved} carrier-unresolved.</p>
        </div>
        {hasPerm("commission.resolve") && (
          <Button variant="outline" size="sm" disabled={busy} onClick={resync} data-testid="cc-resync-btn">
            <ArrowsClockwise size={15} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Resync from snapshots
          </Button>
        )}
      </div>
      <CCNav />

      {data.unresolved > 0 && carrierFilter !== "unresolved" && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 mb-4 flex items-center gap-2 text-sm text-amber-900" data-testid="cc-unresolved-banner">
          <WarningCircle size={18} weight="duotone" />
          {data.unresolved} polic{data.unresolved > 1 ? "ies" : "y"} came from EZLynx with a carrier name that doesn't match your registry — add the carrier or an alias below, then Resync.
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setCarrierFilter("unresolved")} data-testid="cc-show-unresolved-btn">Show</Button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <div className="flex gap-1.5 flex-1 min-w-[220px]">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Policy # or insured name"
                onKeyDown={(e) => e.key === "Enter" && load()} data-testid="cc-policies-search-input" />
              <Button variant="outline" onClick={load} data-testid="cc-policies-search-btn"><MagnifyingGlass size={15} /></Button>
            </div>
            <Select value={carrierFilter} onValueChange={setCarrierFilter}>
              <SelectTrigger className="w-44" data-testid="cc-policies-carrier-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All carriers</SelectItem>
                <SelectItem value="unresolved">Unresolved only</SelectItem>
                {carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {data.policies.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">No policies yet — validate an EZLynx book-of-business snapshot on the Imports tab and they'll appear here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cc-policies-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                    <th className="px-4 py-2">Policy #</th>
                    <th className="px-4 py-2">Carrier</th>
                    <th className="px-4 py-2">Insured</th>
                    <th className="px-4 py-2">Producer</th>
                    <th className="px-4 py-2">Terms</th>
                    <th className="px-4 py-2">Last synced</th>
                  </tr>
                </thead>
                <tbody>
                  {data.policies.map((p) => {
                    const latest = [...p.terms].sort((a, b) => (b.effective_date || "").localeCompare(a.effective_date || ""))[0];
                    return (
                      <tr key={p.id} className="border-b border-slate-50">
                        <td className="px-4 py-2.5 font-mono text-xs">{p.policy_number}</td>
                        <td className="px-4 py-2.5">
                          {p.carrier_id
                            ? carrierName(p.carrier_id)
                            : <Badge className="bg-amber-100 text-amber-800">unresolved: {p.carrier_name_raw || "?"}</Badge>}
                        </td>
                        <td className="px-4 py-2.5 max-w-[180px] truncate">{p.insured_name || "—"}</td>
                        <td className="px-4 py-2.5 text-xs">
                          {p.assigned_producer_raw || <span className="text-slate-400">—</span>}
                          {(p.labels || []).length > 0 && (
                            <div className="flex gap-1 flex-wrap mt-0.5">
                              {p.labels.map((lb) => (
                                <Badge key={lb} className="bg-violet-100 text-violet-800 text-[10px] px-1.5 py-0" data-testid="cc-policy-label">{lb}</Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          {p.terms.length}{latest && <span className="text-slate-400"> · {latest.effective_date} → {latest.expiration_date || "?"}</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-400">{fmtDate(p.last_synced_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <CarrierRulesCard carriers={carriers} rules={rules} onChanged={load} />
      </div>
    </div>
  );
}
